import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthStore, selectUser, canAccessMenuSite, hasMenu, type MenuKey } from "@safers/auth";
import { AlarmInboxBell } from "@/components/AlarmInboxBell";
import { useCurrentSiteId } from "@/hooks/useCurrentSiteId";
import { readLastSite } from "@/lib/lastVisit";
import { areaOf, cctvLivePath, controlPath, healthPath, ME_PATH } from "@/routes/paths";

interface TabDef {
  to: string;
  label: string;
  isActive: (pathname: string) => boolean;
  icon: (active: boolean) => React.ReactNode;
  menu?: MenuKey;
}

function iconProps(active: boolean) {
  return {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: active ? 1.9 : 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
}

function areaTabs(id: string | null): TabDef[] {
  return [
    {
      to: controlPath(id),
      label: "관제",
      menu: "CONTROL",
      isActive: (p) => areaOf(p) === "control",
      icon: (active) => (
        <svg {...iconProps(active)}>
          <rect x="2.5" y="4" width="19" height="12.5" rx="2" />
          <path d="M8.5 20.5h7M12 16.5v4" />
        </svg>
      ),
    },
    {
      to: healthPath(id),
      label: "보건관리",
      menu: "HEALTH",
      isActive: (p) => areaOf(p) === "health",
      icon: (active) => (
        <svg {...iconProps(active)}>
          <path d="M20.4 5.9a5 5 0 0 0-7.1 0L12 7.2l-1.3-1.3a5 5 0 1 0-7.1 7.1l1.3 1.3L12 21.4l7.1-7.1 1.3-1.3a5 5 0 0 0 0-7.1Z" />
        </svg>
      ),
    },
    {
      to: cctvLivePath({ siteId: id }),
      label: "CCTV",
      menu: "CCTV",
      isActive: (p) => areaOf(p) === "cctv",
      icon: (active) => (
        <svg {...iconProps(active)}>
          <path d="M3 8.5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7Z" />
          <path d="m15 11 5-3v8l-5-3v-2Z" />
        </svg>
      ),
    },
  ];
}

const ACCOUNT_TAB: TabDef = {
  to: ME_PATH,
  label: "내정보",
  isActive: (p) => p === ME_PATH,
  icon: (active) => (
    <svg {...iconProps(active)}>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  ),
};

const FAB_SIZE = "3.25rem";
const PROTRUDE = "1rem";
const NOTCH_CENTER_Y = "0.625rem";
const NOTCH_RADIUS = "1.9375rem";
const NOTCH_WIDTH = "3.875rem";
const ARC_BOX_HEIGHT = "2.5625rem";
const ARC_OFFSET = "-1.3125rem";
const FAB_TOP = "-1.5rem";
const FAB_INSET = "0.3125rem";
const NOTCH_MASK = `radial-gradient(circle ${NOTCH_RADIUS} at 50% ${NOTCH_CENTER_Y}, transparent 98%, #000 100%)`;

function useSiteId(): string | null {
  const id = useCurrentSiteId();
  return id == null ? null : String(id);
}

export function BottomTabBar() {
  const { pathname } = useLocation();
  const routeSiteId = useSiteId();
  const user = useAuthStore(selectUser);
  const username = user?.username ?? null;

  const [lastSiteId, setLastSiteId] = useState(() => routeSiteId ?? readLastSite(username));
  if (routeSiteId !== null && routeSiteId !== lastSiteId) setLastSiteId(routeSiteId);
  const siteId = routeSiteId ?? (pathname === ACCOUNT_TAB.to ? lastSiteId : null);

  const allowed = (menu: MenuKey) =>
    siteId ? canAccessMenuSite(user, siteId, menu) : hasMenu(user, menu);
  // 권한 없는 영역은 탭을 뺀다(잠금 표시 대신).
  const tabs = [...areaTabs(siteId).filter((tab) => !tab.menu || allowed(tab.menu)), ACCOUNT_TAB];

  function renderTab(tab: TabDef) {
    const active = tab.isActive(pathname);
    return (
      <Link
        key={tab.to}
        to={tab.to}
        className="relative flex flex-1 flex-col items-center justify-center gap-[0.1875rem]"
        style={{ color: active ? "var(--brand-primary)" : "var(--text-tertiary)" }}
      >
        <span className="relative flex">{tab.icon(active)}</span>
        <span
          className="text-[0.625rem] leading-[0.875rem]"
          style={{ fontWeight: active ? 600 : 500 }}
        >
          {tab.label}
        </span>
      </Link>
    );
  }

  const half = Math.ceil(tabs.length / 2);
  const left = tabs.slice(0, half);
  const right = tabs.slice(half);

  return (
    <div className="relative z-40 flex-none" style={{ marginTop: `-${PROTRUDE}` }}>
      <div className="pointer-events-none" style={{ height: PROTRUDE }} aria-hidden="true" />

      <div className="relative">
        <div
          aria-hidden="true"
          className="absolute inset-0 border-t border-[var(--border-default)] bg-[var(--surface-base)]"
          style={{ maskImage: NOTCH_MASK, WebkitMaskImage: NOTCH_MASK }}
        />
        <div
          className="absolute left-0 right-0 mx-auto overflow-hidden"
          style={{ top: 0, width: NOTCH_WIDTH, height: ARC_BOX_HEIGHT }}
          aria-hidden="true"
        >
          <div
            className="rounded-full border border-[var(--border-default)]"
            style={{ width: NOTCH_WIDTH, height: NOTCH_WIDTH, marginTop: ARC_OFFSET }}
          />
        </div>

        <nav
          className="relative flex items-stretch px-2 pt-2"
          style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}
        >
          <div className="flex flex-1">{left.map(renderTab)}</div>
          <div className="relative flex-none" style={{ width: NOTCH_WIDTH }}>
            <div className="absolute" style={{ top: FAB_TOP, left: FAB_INSET, width: FAB_SIZE }}>
              <AlarmInboxBell variant="fab" />
            </div>
          </div>
          <div className="flex flex-1">{right.map(renderTab)}</div>
        </nav>
      </div>
    </div>
  );
}
